import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { Pencil, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { formatDate, formatCurrency, getExpiryStatus } from "@/lib/utils-date";
import { formatKg, parsePackLabelKg, formatEstimatedPieces } from "@/lib/format";
import MarginPill from "./MarginPill";
import SetPricePill from "./SetPricePill";
import { usePerm } from "@/context/AuthContext";

export default function ProductRow({ product, onSell, onEdit, onDelete, expanded, onToggleExpand }) {
  const canProductsWrite = usePerm("products_write");
  const canSalesWrite = usePerm("sales_write");
  const st = getExpiryStatus(product.expiry_date);
  const hasPrice = Number(product.price_per_kg) > 0 || Number(product.price) > 0;
  const isWeightUnit = product.sale_unit === "weight" || (product.stock_kg !== null && product.stock_kg !== undefined);
  const isOutOfStock = isWeightUnit ? Number(product.stock_kg) <= 0 : Number(product.stock) <= 0;
  const showExpiryBadge = !(isOutOfStock && st.cls !== "badge-expired");
  // Chevron: visibile se c'è almeno un lotto reale (esclude i seed anonimi:
  // batch vuoto + no scadenza + giacenza 0, creati al POST /products).
  const meaningfulLots = Array.isArray(product.lots)
    ? product.lots.filter(
        (l) =>
          !(
            (l.batch || "") === "" &&
            !l.expiry_date &&
            Number(l.stock || 0) === 0 &&
            Number(l.stock_kg || 0) === 0
          )
      )
    : [];
  const canExpand = meaningfulLots.length >= 1;
  return (
    <TableRow
      data-testid={`product-row-${product.id}`}
      className="border-[#EAE7E1] hover:bg-[#F9F8F6]"
    >
      <TableCell className="py-4 w-8 pr-0">
        {canExpand ? (
          <button
            type="button"
            onClick={onToggleExpand}
            data-testid={`product-row-expand-${product.id}`}
            aria-label={expanded ? "Nascondi lotti" : "Mostra lotti"}
            aria-expanded={expanded ? "true" : "false"}
            className="h-6 w-6 inline-flex items-center justify-center rounded-md text-[#5C5F5A] hover:bg-[#F4F1ED] hover:text-[#2E4A3B] transition-colors"
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        ) : (
          <span className="inline-block h-6 w-6" aria-hidden="true" />
        )}
      </TableCell>
      <TableCell className="py-4">
        <div className="font-medium text-[#1A1C1A]">{product.name}</div>
        {product.is_fresh && (
          <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#2B5A3F] bg-[#E8F3EC] border border-[#C5E1D1] px-1.5 py-0.5 rounded">
            Fresco
          </span>
        )}
      </TableCell>
      <TableCell className="text-sm text-[#5C5F5A]">{product.category}</TableCell>
      <TableCell className="text-sm text-[#5C5F5A] font-mono">
        {product.sale_unit === "weight"
          ? (Number(product.stock_kg) > 0 ? formatKg(product.stock_kg) : "—")
          : (product.weight || "—")}
      </TableCell>
      <TableCell className="text-sm text-[#5C5F5A] font-mono">{product.batch || "—"}</TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <span className="text-sm text-[#1A1C1A]">{formatDate(product.expiry_date)}</span>
          {showExpiryBadge && (
            <span className={`${st.cls} inline-block text-[10px] font-semibold px-2 py-0.5 rounded-md w-fit`}>
              {st.label}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        {product.stock_kg !== null && product.stock_kg !== undefined ? (
          <span className="inline-flex items-baseline gap-2">
            <span className={`font-mono text-sm ${product.stock_kg <= 0 ? "text-[#C65D47] font-semibold" : product.stock_kg <= 0.5 ? "text-[#8B6518]" : "text-[#1A1C1A]"}`}>
              {product.stock_kg <= 0 ? "esaurito" : formatKg(product.stock_kg)}
            </span>
            {(() => {
              // Stima pezzi (desktop). Stesse regole della card mobile.
              const est = formatEstimatedPieces(
                product.stock_kg,
                parsePackLabelKg(product.weight),
              );
              return est ? (
                <span
                  data-testid={`product-row-estimated-pieces-${product.id}`}
                  className="font-mono text-xs text-[#5C5F5A]"
                  title="Stima calcolata da giacenza / pezzatura"
                >
                  {est}
                </span>
              ) : null;
            })()}
          </span>
        ) : (
          <span className={`font-mono text-sm ${
            product.min_stock !== null && product.min_stock !== undefined && product.stock <= product.min_stock
              ? "text-[#C65D47] font-semibold"
              : product.stock <= 3
              ? "text-[#C65D47] font-semibold"
              : "text-[#1A1C1A]"
          }`}>
            {product.stock}
            {product.min_stock !== null && product.min_stock !== undefined && (
              <span className="text-[#8E918C] font-normal text-xs"> / min {product.min_stock}</span>
            )}
            {isOutOfStock && <span className="text-[#C65D47] font-semibold"> esaurito</span>}
          </span>
        )}
      </TableCell>
      <TableCell className="text-sm font-mono text-[#1A1C1A]">
        <div className="flex items-center gap-2">
          {hasPrice ? (
            <span>
              {product.price_per_kg
                ? `${formatCurrency(product.price_per_kg)}/kg`
                : formatCurrency(product.price)}
            </span>
          ) : (
            <SetPricePill product={product} onClick={onEdit} size="sm" />
          )}
          <MarginPill product={product} size="xs" />
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          {canSalesWrite && (
          <Button
            data-testid={`sell-product-${product.id}`}
            variant="ghost"
            size="sm"
            onClick={onSell}
            className="text-[#2E4A3B] hover:bg-[#E8F3EC] text-xs font-medium"
          >
            Vendi
          </Button>
          )}
          {canProductsWrite && (
          <Button
            data-testid={`edit-product-${product.id}`}
            variant="ghost"
            size="icon"
            onClick={onEdit}
            className="h-8 w-8 hover:bg-[#F4F1ED]"
          >
            <Pencil size={14} />
          </Button>
          )}
          {canProductsWrite && (
          <Button
            data-testid={`delete-product-${product.id}`}
            variant="ghost"
            size="icon"
            onClick={onDelete}
            className="h-8 w-8 text-[#C65D47] hover:bg-[#FCEAE8]"
          >
            <Trash2 size={14} />
          </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
